import {
  type BookingCompletionBlocker,
  BookingCompletionBlockerCode,
  type BookingCompletionSideEffectIntent,
  BookingCompletionSideEffectKind,
  type BookingCompletionSideEffectsPlan,
  type CarRecordMinimal,
  type TireSeason
} from '@navio/nsdk';
import { getCarDisplayName } from '../car/car';

export type { BookingCompletionSideEffectsPlan };

const SEASON_LABEL: Record<string, string> = {
  SUMMER: 'summer',
  WINTER: 'winter',
  ALL_SEASON: 'all-season'
};

const ACTIONABLE_KINDS = new Set<BookingCompletionSideEffectKind>([
  BookingCompletionSideEffectKind.CUSTODY_DELIVER_TO_CUSTOMER,
  BookingCompletionSideEffectKind.CUSTODY_PICKUP_FROM_CUSTOMER,
  BookingCompletionSideEffectKind.CUSTODY_SKIP_DELIVER_MOUNT_HOLD,
  BookingCompletionSideEffectKind.CUSTODY_VOID_OUTBOUND_LOAD_TASKS,
  BookingCompletionSideEffectKind.CUSTODY_VOID_INBOUND_LOAD_TASKS
]);

const seasonPhrase = (season: TireSeason | null | undefined): string => {
  if (!season) {
    return 'wheel set';
  }
  return `${SEASON_LABEL[season] ?? season.toLowerCase()} wheel set`;
};

const carPhrase = (car: CarRecordMinimal | null | undefined): string => {
  if (!car) {
    return '';
  }
  const display = getCarDisplayName({
    car: {
      make: car.make,
      model: car.model,
      licensePlateNumber: car.licensePlateNumber,
      licensePlate: car.licensePlate
    },
    showYear: false
  });
  return display ? ` for ${display}` : '';
};

const completionSideEffectMessage = (intent: BookingCompletionSideEffectIntent): string => {
  const season = intent.custody?.season;
  const car = intent.custody?.car;
  switch (intent.kind) {
    case BookingCompletionSideEffectKind.CUSTODY_DELIVER_TO_CUSTOMER:
      return `Mark the ${seasonPhrase(season)}${carPhrase(car)} as delivered to the customer`;
    case BookingCompletionSideEffectKind.CUSTODY_PICKUP_FROM_CUSTOMER:
      return `Mark the ${seasonPhrase(season)}${carPhrase(car)} as picked up from the customer`;
    case BookingCompletionSideEffectKind.CUSTODY_SKIP_DELIVER_MOUNT_HOLD:
      return `Skip marking the ${seasonPhrase(season)}${carPhrase(car)} as delivered — it still needs mount work at the facility`;
    case BookingCompletionSideEffectKind.CUSTODY_VOID_OUTBOUND_LOAD_TASKS:
      return 'Cancel the incomplete load of wheel sets onto the vehicle for this booking';
    case BookingCompletionSideEffectKind.CUSTODY_VOID_INBOUND_LOAD_TASKS:
      return 'Cancel the incomplete load of wheel sets from the customer into storage for this booking';
    case BookingCompletionSideEffectKind.CUSTODY_NO_CHANGES:
      return '';
    default: {
      const _exhaustive: never = intent.kind;
      return _exhaustive;
    }
  }
};

export type CompletionBlockerPhase = 'start' | 'complete';

const priorLoadStopsMessage = (phase: CompletionBlockerPhase): string => {
  const beforeAction = phase === 'start' ? 'starting' : 'completing';
  return `There is still a reload stop earlier on this route that is not completed. Please complete it before ${beforeAction} this booking.`;
};

const missingReloadWheelCargoMessage = (phase: CompletionBlockerPhase): string => {
  const beforeAction = phase === 'start' ? 'starting' : 'completing';
  return `Wheel sets from the reload stop earlier on this route are not yet accounted for. Please resolve them before ${beforeAction} this booking.`;
};

const PRIOR_RELOAD_BLOCKER_CODES = new Set<BookingCompletionBlockerCode>([
  BookingCompletionBlockerCode.CUSTODY_PRIOR_LOAD_STOPS_INCOMPLETE,
  BookingCompletionBlockerCode.TIRE_HOTEL_PRIOR_LOAD_STOPS_INCOMPLETE
]);

export const isPriorReloadCompletionBlocker = (blocker: BookingCompletionBlocker): boolean =>
  PRIOR_RELOAD_BLOCKER_CODES.has(blocker.code);

export const completionBlockerMessage = (
  blocker: BookingCompletionBlocker,
  phase: CompletionBlockerPhase = 'complete'
): string => {
  switch (blocker.code) {
    case BookingCompletionBlockerCode.CUSTODY_PRIOR_LOAD_STOPS_INCOMPLETE:
    case BookingCompletionBlockerCode.TIRE_HOTEL_PRIOR_LOAD_STOPS_INCOMPLETE: {
      return priorLoadStopsMessage(phase);
    }
    case BookingCompletionBlockerCode.CUSTODY_MISSING_RELOAD_WHEEL_CARGO: {
      return missingReloadWheelCargoMessage(phase);
    }
    case BookingCompletionBlockerCode.WHEEL_PICKUP_FAILED: {
      return 'Pickup failed. Complete a replacement pickup, record actual receipt, or remove unperformed storage and review settlement before completing.';
    }
    default: {
      const _exhaustive: never = blocker.code;
      return _exhaustive;
    }
  }
};

export const completionSideEffectMessages = (plan: BookingCompletionSideEffectsPlan | null | undefined): string[] => {
  const intents = plan?.intents ?? [];
  return intents
    .filter((intent) => ACTIONABLE_KINDS.has(intent.kind))
    .map(completionSideEffectMessage)
    .filter((message) => message.length > 0);
};
